import { useOrganization } from '@clerk/clerk-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Lock } from 'lucide-react';
import { useRef, useState } from 'react';
import { Control, FieldValues, useForm } from 'react-hook-form';
import { z } from 'zod';

import FileUploadPanel, {
  FileUploadPanelRef,
} from '@/components/global/form-inputs/FileUploadPanel';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { uploadToStorage } from '@/helpers/storage.helper';
import { toast } from '@/hooks/ui/use-toast';
import logger from '@/services/logger.service';

type NoteCategory =
  | 'calls'
  | 'meeting'
  | 'manual-email'
  | 'system-emails'
  | 'firefiles'
  | 'general';

interface UploadedFileData {
  url: string;
  name: string;
  size: number;
  type: string;
}

const AddNotesFormSchema = z.object({
  noteType: z.string().min(1, 'Please select a note type'),
  detailedNotes: z.string().min(1, 'Please enter detailed notes'),
  file: z.any().optional(),
});

interface AddNotesDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSave: (note: {
    title: string;
    content: string;
    category: NoteCategory;
    attachment?: {
      file_name: string;
      file_url: string;
      file_size: number;
      file_type: string;
    };
  }) => Promise<void>;
}

const AddNotesDialog = ({ open, setOpen, onSave }: AddNotesDialogProps) => {
  const { organization } = useOrganization();
  const [noteDetail, setNoteDetail] = useState<string | null>(null);
  const fileUploadRef = useRef<FileUploadPanelRef>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  const form = useForm<z.infer<typeof AddNotesFormSchema>>({
    resolver: zodResolver(AddNotesFormSchema),
    defaultValues: {
      noteType: 'general',
      detailedNotes: '',
      file: undefined,
    },
  });

  const noteTypeOptions = [
    { value: 'general', label: 'General' },
    { value: 'calls', label: 'Calls' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'manual-email', label: 'Manual Email' },
    { value: 'firefiles', label: 'Firefiles' },
  ];

  const handleFileUpload = async (file: File) => {
    setIsUploadingFile(true);
    try {
      const uploadedDocument = await uploadToStorage({
        file,
        bucketName: 'documents',
        folderName: organization?.id ?? '',
      });

      if (uploadedDocument) {
        return {
          url: uploadedDocument.file_url,
          name: file.name,
          size: uploadedDocument.file_size,
          type: file.type,
        };
      }
    } catch (error) {
      logger.error('Failed to upload file attachment', error as Error, {
        fileName: file.name,
      });
      console.log({ error });
      if ((error as Error)?.message?.includes('Invalid key')) {
        toast({
          title: 'Invalid file name or type',
          description:
            'File name contains invalid characters or type is not supported. Please try again with a different file.',
          variant: 'destructive',
        });
        return;
      }
      toast({
        title: 'File upload failed',
        description: 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleSubmit = async (values: z.infer<typeof AddNotesFormSchema>) => {
    let uploadedFile: UploadedFileData | undefined = undefined;

    setIsSubmitting(true);

    if (values.file) {
      uploadedFile = await handleFileUpload(values.file);
      if (!uploadedFile) {
        fileUploadRef.current?.clearFile();
        form.setValue('file', undefined);
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const noteTitle =
        values.noteType === 'general'
          ? 'General Note'
          : values.noteType === 'calls'
            ? 'Call Note'
            : values.noteType === 'meeting'
              ? 'Meeting Note'
              : values.noteType === 'manual-email'
                ? 'Manual Email Note'
                : values.noteType === 'firefiles'
                  ? 'Firefiles Note'
                  : 'Note';

      const noteData = {
        title: noteTitle,
        content: values.detailedNotes,
        category: values.noteType as NoteCategory,
        ...(uploadedFile && {
          attachment: {
            file_name: uploadedFile.name,
            file_url: uploadedFile.url,
            file_size: uploadedFile.size,
            file_type: uploadedFile.type,
          },
        }),
      };

      await onSave(noteData);

      // Reset form and state
      form.reset({
        noteType: 'general',
        detailedNotes: '',
        file: undefined,
      });
      setOpen(false);
    } catch (error) {
      console.error('Error saving note:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDialogOpenChange = (newOpen: boolean) => {
    if (!newOpen && !isSubmitting) {
      // Reset form and state when closing
      form.reset({
        noteType: 'general',
        detailedNotes: '',
        file: undefined,
      });
    }
    setOpen(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="w-[480px] bg-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Add New Notes
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-6"
          >
            {/* Note Type Field */}
            <FormField
              control={form.control}
              name="noteType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-gray-700">
                    Note Type
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Select note type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {noteTypeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Detailed Notes Field */}
            <FormField
              control={form.control}
              name="detailedNotes"
              render={({ field }) => {
                if (field?.value && field?.value !== '') {
                  setNoteDetail(field.value);
                } else {
                  setNoteDetail(null);
                }
                return (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-gray-700">
                      Detailed Notes
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Enter detail notes about the interaction...."
                        className="min-h-[100px] resize-none"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            {/* File Upload Field */}
            <FormField
              control={form.control}
              name="file"
              render={() => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-gray-700">
                    Attachment (Optional)
                  </FormLabel>
                  {noteDetail ? (
                    <FileUploadPanel
                      ref={fileUploadRef}
                      name="file"
                      control={form.control as unknown as Control<FieldValues>}
                      supportedFormatsText="PDF, JPEG, PNG, Excel files"
                      acceptedFileTypes={{
                        'application/pdf': ['.pdf'],
                        'image/jpeg': ['.jpeg', '.jpg'],
                        'image/png': ['.png'],
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                          ['.xlsx'],
                        'application/vnd.ms-excel': ['.xls'],
                      }}
                    />
                  ) : (
                    <div className="cursor-not-allowed rounded-lg border-2 border-dashed border-zinc-300 bg-white p-4 transition-colors">
                      <div className="flex h-[160px] flex-col items-center justify-center">
                        <Lock className="h-10 w-10" />
                        <p className="as-body-03 mt-2 items-center justify-center text-gray-500">
                          First add some details to the note
                        </p>
                      </div>
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Save Button */}
            <div className="w-full">
              <Button
                type="submit"
                disabled={isSubmitting || isUploadingFile}
                className="w-full rounded-md bg-sky-400 py-2 text-white hover:bg-sky-500 disabled:opacity-50"
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddNotesDialog;
